import React, {useEffect, useRef, useState} from 'react'
import {WeekDays, WeekDaysPicker} from '@therms/atalaya'
import {
  AvailabilityWeek,
  daysArrayToWeek,
  PreferredDay,
  weekDaysFirstDay,
  weekToDaysArray,
} from '@therms/react-modules'
import {
  PendingApprovalPill,
  SectionShell,
} from '@src/modules/Scheduling/components/SectionShell'

// One submit per burst of day clicks, not one per click.
const SUBMIT_DEBOUNCE_MS = 800

interface PreferredDaysSectionProps {
  canEdit: boolean
  /** Live (approved) days, shown for reference while a change is pending */
  currentDays?: PreferredDay[]
  days?: PreferredDay[]
  /** The primary region, when its days fall differently from the device's. */
  daysZoneLabel?: React.ReactNode
  /** The region's first day of the week; the days start on it. */
  firstDayOfWeek?: string
  note?: React.ReactNode
  onChangeDays: (days: PreferredDay[]) => void
  pending?: boolean
}

export function PreferredDaysSection({
  canEdit,
  currentDays,
  days,
  daysZoneLabel,
  firstDayOfWeek,
  note,
  onChangeDays,
  pending,
}: PreferredDaysSectionProps) {
  const [localDays, setLocalDays] = useState<PreferredDay[]>(days ?? [])
  const submitTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    setLocalDays(days ?? [])
  }, [days])

  useEffect(() => () => window.clearTimeout(submitTimer.current), [])

  function handleChange(week: AvailabilityWeek) {
    const nextDays = weekToDaysArray(week)
    setLocalDays(nextDays)
    window.clearTimeout(submitTimer.current)
    submitTimer.current = window.setTimeout(() => {
      onChangeDays(nextDays)
    }, SUBMIT_DEBOUNCE_MS)
  }

  const firstDay = weekDaysFirstDay(firstDayOfWeek)
  const zoneNote = daysZoneLabel && (
    <span className="text-color-subtle text-xs">Days in {daysZoneLabel} time</span>
  )

  const valueControl = canEdit ? (
    <WeekDaysPicker
      firstDayOfWeek={firstDay}
      fullWidth
      onChangeValue={handleChange}
      value={daysArrayToWeek(localDays)}
    />
  ) : (
    <WeekDays
      firstDayOfWeek={firstDay}
      fullWidth
      value={daysArrayToWeek(localDays)}
    />
  )

  return (
    <SectionShell title="Preferred Days">
      {pending ? (
        <>
          <div className="mb-sm gap-xxs flex flex-col items-start">
            <span className="text-color-subtle text-xs font-semibold uppercase opacity-80">
              Current
            </span>
            <div className="w-[280px]">
              <WeekDays
                firstDayOfWeek={firstDay}
                fullWidth
                value={daysArrayToWeek(currentDays)}
              />
            </div>
          </div>

          <div className="border-caution gap-sm p-sm flex flex-col items-start rounded-lg border">
            <div className="gap-sm flex items-center">
              <div className="w-[280px]">{valueControl}</div>
              <PendingApprovalPill />
            </div>
            {zoneNote}
            {note && <div className="text-color-subtle text-xs">{note}</div>}
          </div>
        </>
      ) : (
        <div className="gap-sm flex flex-col items-start">
          {canEdit && !localDays.length && (
            <span className="text-color-subtle text-sm">
              {
                "Choose the days you'd prefer to work. Managers see them as a hint, not a rule."
              }
            </span>
          )}
          {canEdit || localDays.length ? (
            <div className="w-[280px]">{valueControl}</div>
          ) : (
            <span className="text-color-subtle text-sm">Not set.</span>
          )}
          {!!localDays.length && zoneNote}
        </div>
      )}
    </SectionShell>
  )
}
